/**
 * AI Builder + versioning API. Added for the FE↔BE integration (Rounds 8+9).
 *
 * The builder's lifecycle is the real version-management path:
 *   POST /builder/ingest                      — ingest a vendor model → DRAFT version + operations
 *   GET  /builder/versions/:versionId/operations — list a version's operations
 *   POST /builder/versions/:versionId/approve — approve = PUBLISH the draft (go live)
 *
 * Plus the standalone versioning ops (Round 8):
 *   GET  /versioning/versions?integration_id= — list adapter versions
 *   POST /versioning/versions/:versionId/publish
 *   POST /versioning/versions/:versionId/deprecate
 *
 * All are Softknack-internal (internal=true).
 *
 * WHY ingest is the authentic version story: the BE seed ships a single
 * published version per connector (no draft). A real DRAFT to approve only
 * exists once you ingest a vendor model — so the genuine end-to-end
 * draft→approve→live flow runs through /builder/ingest, not the FE's local
 * synthesized version history (which stays as a demo for already-seeded
 * connectors).
 */
import { apiFetch } from './client';

export interface BeVersionRow {
  version_id: string;
  vendor_version: string;
  status: string; // draft | published | deprecated
  tag: string; // live | n_1 | none
  rev: number;
  exec_count: number;
}

export interface IngestSourceInput {
  // Exactly one of mcp/openapi/prose, plus optional auth/change_delivery.
  mcp?: { tools: Array<{ name: string; entity?: string }> };
  openapi?: Record<string, unknown>;
  prose?: string;
  auth?: { kind: string; scopes?: string[] };
  change_delivery?: 'none' | 'webhook' | 'poll';
}

export interface IngestResultBe {
  // Shape returned by ingestAndDraft (version + drafted operations + confidence).
  version_id: string;
  [k: string]: unknown;
}

/** Ingest a vendor model → create a draft version + operations on the backend. */
export async function ingestVendor(
  slug: string,
  displayName: string,
  source: IngestSourceInput,
  vendorVersion?: string,
): Promise<IngestResultBe> {
  return apiFetch<IngestResultBe>('/builder/ingest', {
    method: 'POST',
    body: {
      slug,
      display_name: displayName,
      ...(vendorVersion ? { vendor_version: vendorVersion } : {}),
      source,
    },
    internal: true,
  });
}

/** List operations drafted for a version. */
export async function listOperations(versionId: string): Promise<unknown[]> {
  const res = await apiFetch<{ operations: unknown[] }>(`/builder/versions/${versionId}/operations`, {
    internal: true,
  });
  return res.operations;
}

/** Approve (publish) a draft version via the builder surface. */
export async function approveVersion(versionId: string): Promise<unknown> {
  return apiFetch(`/builder/versions/${versionId}/approve`, { method: 'POST', internal: true });
}

// --- Standalone versioning (Round 8) ---

/** List adapter versions for an integration (BE shape). */
export async function listVersions(integrationId: string): Promise<BeVersionRow[]> {
  const res = await apiFetch<{ versions: BeVersionRow[] }>(
    `/versioning/versions?integration_id=${encodeURIComponent(integrationId)}`,
    { internal: true },
  );
  return res.versions;
}

/** Publish (go live) a draft adapter version. */
export async function publishVersion(versionId: string): Promise<unknown> {
  return apiFetch(`/versioning/versions/${versionId}/publish`, { method: 'POST', internal: true });
}

/** Deprecate an adapter version. */
export async function deprecateVersion(versionId: string): Promise<unknown> {
  return apiFetch(`/versioning/versions/${versionId}/deprecate`, { method: 'POST', internal: true });
}

// --- Mapping Plane: AI-proposed canonical→vendor operation mapping ---
// The BE orchestrates everything server-side (fetch live vendor tools, propose
// via Claude, validate against real capabilities, dry-run probe). The FE just
// renders the result for a human to approve.

export interface MappingFieldRule {
  from?: string;
  to: string;
  const?: unknown;
}
export interface MappingDraft {
  canonical_tool: string;
  vendor_tool: string | null;
  fields: MappingFieldRule[];
  confidence: number;
  reasoning: string;
  proposer: string;
}
export interface MappingAutoResult {
  canonical_tool: string;
  entity_key: string;
  is_write: boolean;
  vendor_tool_count: number;
  draft: MappingDraft;
  validation: { ok: boolean; errors: string[] };
  probe: { ran: boolean; ok: boolean; detail: string } | null;
}

/**
 * Ask the BE to auto-propose a mapping for one canonical tool over a live
 * connection. Returns the proposal + validation + (read-only) probe result.
 */
export async function autoMapConnection(connectionId: string, canonical: string): Promise<MappingAutoResult> {
  return apiFetch<MappingAutoResult>(
    `/connections/${connectionId}/mapping/auto?canonical=${encodeURIComponent(canonical)}`,
    { internal: true },
  );
}

/**
 * Approve a validated mapping draft. The BE re-fetches live tools and
 * re-validates server-side before persisting — the FE just echoes the draft it
 * was shown plus which canonical tool it's for.
 */
export async function approveMapping(
  connectionId: string,
  canonical: string,
  draft: MappingDraft,
): Promise<{ version_id: string; operation_count: number }> {
  return apiFetch<{ version_id: string; operation_count: number }>(
    `/connections/${connectionId}/mapping/approve`,
    { method: 'POST', internal: true, body: { canonical, draft } },
  );
}

// --- Whole-entity mapping (AI Integration Builder canvas) ---
// Counterpart to the tool-level mapping/auto+approve above, at FIELD
// granularity: every canonical field gets N ranked vendor-field candidates
// (not one tool-level winner) — what the builder canvas's confidence bars +
// candidate lists actually render.

export interface EntityFieldCandidate {
  vendorField: string;
  confidence: number;
  note: string;
}
export interface EntityFieldMapping {
  canonicalField: string;
  transform: 'direct' | 'coerce' | 'merge' | 'split' | 'passthrough' | 'default';
  candidates: EntityFieldCandidate[];
}
export interface EntityMappingDraft {
  entityKey: string;
  vendorSchemaFields: string[];
  vendorFieldTypes: Record<string, string>;
  fields: EntityFieldMapping[];
  proposer: string;
}
export interface EntityMappingAutoResult {
  entity_key: string;
  vendor_field_count: number;
  draft: EntityMappingDraft;
  validation: { ok: boolean; errors: string[] };
}

/** Ask the BE to propose a whole-entity mapping (every field, ranked candidates). */
export async function autoMapEntity(connectionId: string, entityKey: string): Promise<EntityMappingAutoResult> {
  return apiFetch<EntityMappingAutoResult>(
    `/connections/${connectionId}/entity-mapping/auto?entity=${encodeURIComponent(entityKey)}`,
    { internal: true },
  );
}

/** Approve one direction's field choices for a whole entity. */
export async function approveEntityMapping(
  connectionId: string,
  entityKey: string,
  direction: 'outbound' | 'inbound',
  vendorVersion: string,
  fields: Array<{ canonicalField: string; vendorField: string }>,
): Promise<{ version_id: string; operation_count: number }> {
  return apiFetch<{ version_id: string; operation_count: number }>(
    `/connections/${connectionId}/entity-mapping/approve`,
    {
      method: 'POST',
      internal: true,
      body: { entity_key: entityKey, direction, vendor_version: vendorVersion, fields },
    },
  );
}
